import { contentTexture } from "./content_textures"
import { Container, Sprite, Texture } from "pixi.js"
import { TileSize } from "game-common/models"
import { roughToFineCoordinates } from "game-common/util"
import { SpritePool } from "./sprite_pool"

export interface SpriteManagementServiceConfig {
    spritePoolSize: number
    defaultSurface: Container
}

export class SpriteManagementService {
    private spritePool: SpritePool
    private defaultSurface: Container

    constructor(config: SpriteManagementServiceConfig) {
        this.spritePool = new SpritePool(config.spritePoolSize)
        this.defaultSurface = config.defaultSurface
    }

    /** Where unparented pool sprites go: the map's surface moves when a floor changes roles. */
    setDefaultSurface = (surface: Container): void => {
        this.defaultSurface = surface
    }

    /**
     * Attach a texture to a sprite and add it to the specified surface
     */
    attachTexture = (texture: Texture, surface?: Container): Sprite => {
        const spritePoolItem = this.spritePool.checkout()
        const sprite = spritePoolItem.data
        sprite.texture = texture
        // Reset the occluder-fade tag on every checkout so a recycled sprite can't keep a stale "I am a
        // tree / an ore block" policy from a prior use; CompositeTileRenderer stamps it back on for the
        // tiles that fade (see occluderFadePolicy).
        ;(sprite as any).occluderFade = undefined
        // Same reasoning for the wind's shear: GrassSway leans a blade by writing skew.x, and these
        // sprites are POOLED. A blade that is released mid-gust and handed out again as a wall or a
        // tree would arrive already bent, with nothing to straighten it.
        sprite.skew.x = 0
        ;(sprite as any).grassBlade = undefined
        sprite.scale.set(1, 1)
        sprite.rotation = 0
        const surfaceToUse = surface || this.defaultSurface
        surfaceToUse.addChild(sprite)
        spritePoolItem.data = sprite

        return sprite
    }

    /**
     * Creates a sprite at the specified coordinates with consistent positioning and z-index calculation
     */
    createSpriteAt = (
        tilesetTileId: string,
        x: number,
        y: number,
        zIndexOffset: number = 0,
        surface?: Container,
    ): Sprite => {
        const texture = contentTexture(`${tilesetTileId}`)
        const sprite = this.attachTexture(texture, surface)
        const fine = roughToFineCoordinates({ x, y })
        sprite.x = fine.x
        sprite.y = fine.y
        sprite.zIndex = 100 + zIndexOffset + fine.y + TileSize / 3
        return sprite
    }

    /**
     * Release all sprites back to the pool
     */
    releaseAllSprites = (onRelease?: (spriteItemObject: { data: Sprite }) => void): number => {
        return this.spritePool.releaseAll(spriteItemObject => {
            const sprite = spriteItemObject.data
            if (!sprite.parent) {
                spriteItemObject.data = new Sprite()
                return true // Indicates error
            } else {
                sprite.parent?.removeChild(sprite)
                return false // No error
            }
        })
    }

    /**
     * Get the current sprite pool for advanced operations
     */
    getSpritePool = (): SpritePool => {
        return this.spritePool
    }

    /**
     * Clean up resources
     */
    destroy = (): void => {
        this.spritePool.releaseAll()
    }
}
